import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import {
  useDeleteAdvisorSessionMutation,
  useRenameAdvisorSessionMutation,
} from '@shared/api';
import type { AdvisorSession } from '@entities/progress';
import { IconCheck, IconPencil, IconTrash, IconX } from '@shared/ui/icons';

/* Список своих чатов Эксперта: недавние сверху, группы «Сегодня / Вчера /
   За неделю / Раньше». Переименование и удаление — прямо в строке. */

const Group = styled.div`
  & + & {
    margin-top: ${({ theme }) => theme.spacing(5)};
  }
`;

const GroupTitle = styled.div`
  padding: ${({ theme }) => `0 ${theme.spacing(3)} ${theme.spacing(1.5)}`};
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/* Карандаш и корзина — только у строки под курсором и у открытого чата: иначе
   невидимые кнопки съедают место, и названия обрезаются вдвое раньше. */
const Actions = styled.div`
  display: none;
  gap: 2px;
`;

const Item = styled.div<{ $active: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  padding-right: ${({ theme }) => theme.spacing(1)};
  border-radius: ${({ theme }) => theme.radius.md};
  transition: background ${({ theme }) => theme.motion.fast};

  &:hover,
  &:focus-within {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
  &:hover ${Actions}, &:focus-within ${Actions} {
    display: flex;
  }
  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.colors.primarySoft};
      &:hover {
        background: ${theme.colors.primarySoft};
      }
      ${Actions} {
        display: flex;
      }
    `}
`;

const ItemLink = styled(Link)`
  flex: 1;
  min-width: 0;
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    background: ${({ theme }) => theme.colors.backgroundElevated};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const RenameForm = styled.form`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: ${({ theme }) => theme.spacing(1)};

  input {
    flex: 1;
    min-width: 0;
    padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
    border-radius: ${({ theme }) => theme.radius.xs};
    border: 1px solid ${({ theme }) => theme.colors.borderStrong};
    background: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.text.primary};
    font: inherit;
    font-size: 13px;
  }
`;

const Confirm = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2.5)}`};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.status.danger};
`;

const Empty = styled.div`
  padding: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

function groupOf(iso: string, now: Date): string {
  const d = new Date(iso);
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86_400_000);
  if (diff <= 0) return 'Сегодня';
  if (diff === 1) return 'Вчера';
  if (diff < 7) return 'За неделю';
  if (diff < 31) return 'За месяц';
  return 'Раньше';
}

function SessionRow({
  s,
  active,
  onDeleted,
}: {
  s: AdvisorSession;
  active: boolean;
  onDeleted: (id: string) => void;
}) {
  const [mode, setMode] = useState<'view' | 'rename' | 'delete'>('view');
  const [title, setTitle] = useState(s.title);
  const [rename, { isLoading: renaming }] = useRenameAdvisorSessionMutation();
  const [remove, { isLoading: removing }] = useDeleteAdvisorSessionMutation();

  async function submitRename(e: FormEvent) {
    e.preventDefault();
    const t = title.trim();
    if (!t || t === s.title) {
      setMode('view');
      return;
    }
    try {
      await rename({ id: s.id, title: t }).unwrap();
    } finally {
      setMode('view');
    }
  }

  async function confirmDelete() {
    try {
      await remove(s.id).unwrap();
      onDeleted(s.id);
    } finally {
      setMode('view');
    }
  }

  if (mode === 'rename') {
    return (
      <Item $active={active}>
        <RenameForm onSubmit={submitRename}>
          <input
            autoFocus
            value={title}
            maxLength={120}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setTitle(s.title);
                setMode('view');
              }
            }}
            aria-label="Название чата"
          />
          <IconBtn type="submit" disabled={renaming} aria-label="Сохранить название">
            <IconCheck size={14} />
          </IconBtn>
        </RenameForm>
      </Item>
    );
  }

  if (mode === 'delete') {
    return (
      <Item $active={active}>
        <Confirm>
          Удалить чат?
          <span>
            <IconBtn type="button" onClick={confirmDelete} disabled={removing} aria-label="Да, удалить">
              <IconCheck size={14} />
            </IconBtn>
            <IconBtn type="button" onClick={() => setMode('view')} aria-label="Не удалять">
              <IconX size={14} />
            </IconBtn>
          </span>
        </Confirm>
      </Item>
    );
  }

  return (
    <Item $active={active}>
      <ItemLink
        to={`/expert/${s.id}`}
        aria-current={active ? 'page' : undefined}
        title={s.title}
      >
        {s.title}
      </ItemLink>
      <Actions>
        <IconBtn type="button" onClick={() => setMode('rename')} aria-label="Переименовать чат">
          <IconPencil size={14} />
        </IconBtn>
        <IconBtn type="button" onClick={() => setMode('delete')} aria-label="Удалить чат">
          <IconTrash size={14} />
        </IconBtn>
      </Actions>
    </Item>
  );
}

export function SessionList({
  sessions,
  activeId,
  emptyText = 'Чатов пока нет — задайте первый вопрос.',
  onDeleted,
}: {
  sessions: AdvisorSession[] | undefined;
  activeId: string | undefined;
  /** Что сказать, когда список пуст (например, поиск ничего не нашёл). */
  emptyText?: string;
  onDeleted: (id: string) => void;
}) {
  if (!sessions) return <Empty>Загрузка…</Empty>;
  if (sessions.length === 0) return <Empty>{emptyText}</Empty>;
  const now = new Date();
  const groups: { title: string; items: AdvisorSession[] }[] = [];
  for (const s of sessions) {
    const g = groupOf(s.updated_at, now);
    const last = groups[groups.length - 1];
    if (last && last.title === g) last.items.push(s);
    else groups.push({ title: g, items: [s] });
  }
  return (
    <>
      {groups.map((g) => (
        <Group key={g.title}>
          <GroupTitle>{g.title}</GroupTitle>
          {g.items.map((s) => (
            <SessionRow key={s.id} s={s} active={s.id === activeId} onDeleted={onDeleted} />
          ))}
        </Group>
      ))}
    </>
  );
}
